import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import type { U3129FindProjectItem } from '../../../u3129Timesheet/U3129Service';
import U2000TagAutocomplete from '../../../u2000Core/components/tables/commons/search/U2000TagAutocomplete';
import { NS } from '../../U3125';
import { useU3125FindProjectHandlers } from '../../U3125UseFindProjectHandlers';
import type { U3125ForfaitWizardProjectTag } from '../../U3125Service';

export type U3125ProjectNumberTagInputProps = {
  value: U3125ForfaitWizardProjectTag[];
  onChange: (tags: U3125ForfaitWizardProjectTag[]) => void;
  maxTags?: number;
  disabled?: boolean;
  placeholder?: string;
};

function tagKey(tag: U3125ForfaitWizardProjectTag): string {
  return tag.projetNumber.trim();
}

function projectToWizardTag(item: U3129FindProjectItem): U3125ForfaitWizardProjectTag {
  return {
    projetNumber: String(item.projetNumber ?? '').trim(),
    projectId: item.id,
    projetName: item.projetName,
    customerName: item.customerName,
    freeText: false,
  };
}

function wizardTagToCriteriaTag(tag: U3125ForfaitWizardProjectTag) {
  return { value: tagKey(tag), label: tag.projetNumber, meta: tag };
}

function criteriaTagToWizardTag(tag: { value: string; label?: string; meta?: unknown }): U3125ForfaitWizardProjectTag {
  if (tag.meta && typeof tag.meta === 'object' && 'projetNumber' in (tag.meta as object)) {
    return tag.meta as U3125ForfaitWizardProjectTag;
  }
  return { projetNumber: tag.value, freeText: true };
}

const U3125ProjectNumberTagInput: React.FC<U3125ProjectNumberTagInputProps> = ({
  value,
  onChange,
  maxTags = 50,
  disabled = false,
  placeholder,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { search } = useU3125FindProjectHandlers();

  const criteriaTags = value.map((tag) => wizardTagToCriteriaTag(tag));

  const handleChange = useCallback(
    (tags: { value: string; label?: string; meta?: unknown }[]) => {
      onChange(tags.map((tag) => criteriaTagToWizardTag(tag)));
    },
    [onChange],
  );

  return (
    <U2000TagAutocomplete<U3129FindProjectItem>
      value={criteriaTags}
      onChange={handleChange}
      maxTags={maxTags}
      disabled={disabled}
      allowFreeText
      placeholder={placeholder ?? t('wizardTagPlaceholder', { ns: NS })}
      searchingLabel={t('wizardTagSearching', { ns: NS })}
      noResultsLabel={(text) => t('wizardTagNoResults', { ns: NS, text })}
      searchCriterionField="projetNumber"
      onSearch={search}
      getTagKey={(tag) => tag.value}
      getTagLabel={(tag) => tag.label ?? tag.value}
      getTagTitle={(tag) => {
        const wizard = value.find((w) => tagKey(w) === tag.value);
        if (wizard?.freeText) {
          return t('wizardTagUnverifiedTooltip', { ns: NS });
        }
        return [wizard?.projetName, wizard?.customerName].filter(Boolean).join(' · ') || tag.value;
      }}
      itemToTag={(item) => {
        const wizard = projectToWizardTag(item);
        if (!wizard.projetNumber) return null;
        return wizardTagToCriteriaTag(wizard);
      }}
      renderSuggestion={(item) => (
        <>
          <span className="font-semibold">{item.projetNumber}</span>
          {(item.projetName || item.customerName) && (
            <span className="ml-2 text-gray-600">{[item.projetName, item.customerName].filter(Boolean).join(' — ')}</span>
          )}
        </>
      )}
      onMaxTagsError={(max) => toast.error(t('errWizardMaxProjects', { ns: NS, max }))}
      onDuplicateError={() => toast.error(t('errWizardDuplicateProject', { ns: NS }))}
      footerText={t('wizardProjectCount', { ns: NS, count: value.length, max: maxTags })}
      removeTagAriaLabel={(tag) => t('wizardTagRemoveAria', { ns: NS, number: tag.value })}
    />
  );
};

export default U3125ProjectNumberTagInput;
